// ChartMeasure[] + Arrangement → alphaTex string + per-measure tick map. The PURE STRING SEAM:
// no alphaTab API calls here. Ported from poc/web/app.js buildChartTex/lickTexParts/noteEffect.

import type { Arrangement, ChartMeasure, NoteEvent, TechniqueId } from "../../types/domain";
import { fxList } from "../../types/domain";
import { userBeats } from "./beatGrouping";

export function noteDurSymbol(d: number): string {
  return d === 4 ? "♩" : d === 8 ? "♪" : "♬";
}

export const FX_INNER: Record<string, string> = {
  b: "b (0 4)", bh: "b (0 2)", bq: "b (0 1)", br: "b (0 4 0)",
  bup: "b (0 4)", bdn: "b (4 0)",
  h: "h",
  sl: "sl", ss: "ss",
  sib: "sib", sia: "sia", sou: "sou", sod: "sod",
  v: "v", vw: "vw",
  pm: "pm", lr: "lr", st: "st", ac: "ac", x: "x",
  nh: "nh", ah: "ah", ph: "ph",
  tt: "tt", tp: "tp (8)",
};
export const FX_LABEL: Record<string, string> = {
  b: "풀벤드", bh: "하프벤드", bq: "쿼터벤드", br: "벤드릴리즈", bup: "벤드↗", bdn: "릴리즈↘",
  h: "H/P", sl: "슬라", ss: "시프트슬라", sib: "슬인↗", sia: "슬인↘", sou: "슬아웃↗", sod: "슬아웃↘",
  v: "비브", vw: "와이드비브", pm: "팜뮤트", lr: "레링", st: "스타카토", ac: "악센트", x: "데드음",
  nh: "내추럴HM", ah: "인공HM", ph: "피킹HM", tt: "태핑", tp: "트레몰로", tr: "트릴",
};
export function fxLabel(fx: TechniqueId | null | undefined): string {
  return (fx && FX_LABEL[fx]) || "";
}

export function noteEffect(n: NoteEvent): string {
  if (n.grace) return n.graceSlide ? "{gr sl}" : "{gr h}";
  const t: string[] = [];
  for (const fx of fxList(n.fx)) {
    if (fx === "tr") t.push(`tr (${(+(n.fret || 0)) + 2} 16)`);
    else if (FX_INNER[fx]) t.push(FX_INNER[fx]);
  }
  if (n.tie) t.push("t");
  if (n.ghost) t.push("g");
  return t.length ? `{${t.join(" ")}}` : "";
}

export function withChord(token: string, chord: string | null | undefined): string {
  if (!chord) return token;
  const ch = `ch "${chord}"`;
  return token.endsWith("}") ? token.slice(0, -1) + " " + ch + "}" : token + `{${ch}}`;
}

// merge a beat-level effect (e.g. "d" = dotted) into the token's effect braces
export function withBeatEffect(token: string, eff: string | null | undefined): string {
  if (!eff) return token;
  return token.endsWith("}") ? token.slice(0, -1) + " " + eff + "}" : token + `{${eff}}`;
}

// tex tokens (with 4/4 bar breaks) for a measure's raw notes; chord labels the first beat.
export function lickTexParts(raw: NoteEvent[], chord?: string | null): { body: string; bars: number } {
  const groups: { dur: number; rest: boolean; items: { n: NoteEvent }[] }[] = [];
  raw.forEach((n) => {
    if (n.chord && groups.length && !n.rest) groups[groups.length - 1].items.push({ n });
    else groups.push({ dur: n.dur, rest: !!n.rest, items: [{ n }] });
  });
  const parts: string[] = [];
  let cur: number | null = null, acc = 0, bars = 1, first = true;
  const tok = (it: { n: NoteEvent }) => `${it.n.fret}.${it.n.string}${noteEffect(it.n)}`;
  for (const g of groups) {
    if (g.dur !== cur) { parts.push(`:${g.dur}`); cur = g.dur; }
    const dotted = !!g.items[0].n.dotted;
    const triplet = !!g.items[0].n.triplet;
    let t = g.rest ? "r" : (g.items.length === 1 ? tok(g.items[0]) : `(${g.items.map(tok).join(" ")})`);
    const lab = g.items[0].n.chordLabel || (first ? chord : null);
    t = withChord(t, lab); first = false;
    if (dotted) t = withBeatEffect(t, "d");
    if (triplet) t = withBeatEffect(t, "tu 3");
    parts.push(t);
    if (!(g.items && g.items[0].n.grace)) acc += (4 / g.dur) * (dotted ? 1.5 : 1) * (triplet ? 2 / 3 : 1);
    if (acc >= 4 - 1e-6) { acc -= 4; parts.push("|"); bars++; }
  }
  while (parts[parts.length - 1] === "|") { parts.pop(); bars--; }
  return { body: parts.join(" "), bars: Math.max(1, bars) };
}

export interface BuildChartTexInput {
  chartMeasures: ChartMeasure[];
  arrangement: Arrangement;
  beatLevelMul: number;
  analysisBpm: number;
  preview?: { m: number; notes: NoteEvent[] } | null;
}
export interface TickSpan { startTick: number; durTick: number }
export interface BuildChartTexResult {
  tex: string;
  tickMap: TickSpan[];     // indexed by measure
  headerTempo: number;
}

export function buildChartTex(input: BuildChartTexInput): BuildChartTexResult {
  const { chartMeasures, arrangement, beatLevelMul, analysisBpm, preview } = input;
  const edit = (m: number) => arrangement.measures[m];
  const tsArr = (m: number) => edit(m)?.timeSignature;
  const tempoArr = (m: number) => edit(m)?.tempo;
  const chordArr = (m: number) => edit(m)?.chord;
  const notesArr = (m: number) => edit(m)?.notes;

  const barTicks = ([num, den]: [number, number]) => Math.round((num * 3840) / den);
  const realBeats = (m: number) =>
    (tsArr(m) ? tsArr(m)![0] : (chartMeasures[m] && chartMeasures[m].beats ? chartMeasures[m].beats.length : 4)) || 4;
  const exactTempo = (m: number): number | null => {
    const mm = chartMeasures[m]; if (!mm) return null;
    const dur = mm.endTime - mm.startTime;
    if (!(dur > 0.05)) return null;
    return Math.round((240 * realBeats(m)) / (4 * dur));
  };
  const TEMPO_THRESH = 12 * beatLevelMul;
  const allT = chartMeasures.map((_, i) => exactTempo(i)).filter((x): x is number => x != null).sort((a, b) => a - b);
  const medianTempo = allT.length ? allT[Math.floor(allT.length / 2)] : null;
  const bcCounts: Record<number, number> = {};
  chartMeasures.forEach((_, i) => { const n = realBeats(i); bcCounts[n] = (bcCounts[n] || 0) + 1; });
  let domBeats = 4, domBest = -1;
  for (const k in bcCounts) if (bcCounts[k] > domBest) { domBest = bcCounts[k]; domBeats = +k; }
  if (domBeats !== 3) domBeats = 4;
  const tsOf = (m: number): [number, number] => tsArr(m) || [domBeats, 4];
  const headerTempo = (tempoArr(0) != null ? tempoArr(0)! : medianTempo) || Math.round(analysisBpm || 120);
  let curTempo = headerTempo;
  const firstChord = (m: number): string | null =>
    chordArr(m) ?? (chartMeasures[m].beats.find((b) => b.changed) || {}).chord ?? null;

  const out: string[] = [];
  const tickMap: TickSpan[] = chartMeasures.map(() => ({ startTick: 0, durTick: 3840 }));
  let curTsKey = "4/4", tick = 0;
  for (let m = 0; m < chartMeasures.length; m++) {
    const explicitTempo = tempoArr(m) != null;
    const bp = explicitTempo ? tempoArr(m)! : exactTempo(m);
    const sane = bp != null && (explicitTempo || (!!medianTempo && bp >= medianTempo * 0.82 && bp <= medianTempo * 1.2));
    const thr = explicitTempo ? 1 : TEMPO_THRESH;
    if (sane && Math.abs((bp as number) - curTempo) >= thr) { out.push(`\\tempo ${bp}`); curTempo = bp as number; }

    const mm = chartMeasures[m];
    const ts = tsOf(m), tsKey = ts[0] + "/" + ts[1];
    if (tsKey !== curTsKey) { out.push(`\\ts ${ts[0]} ${ts[1]}`); curTsKey = tsKey; }

    let notes = notesArr(m);
    if (preview && m === preview.m) notes = preview.notes;
    if (notes && notes.length) {
      out.push(lickTexParts(notes, firstChord(m)).body);
    } else {
      out.push(`:${ts[1]}`);
      for (let b = 0; b < ts[0]; b++) {
        let label: string | null = null;
        if (tsArr(m)) label = b === 0 ? firstChord(m) : null;
        else {
          const bt = mm.beats[b];
          label = bt && bt.changed ? bt.chord : null;
          if (b === 0 && chordArr(m) != null) label = chordArr(m)!;
        }
        out.push(label ? `r{ch "${label}"}` : "r");
      }
    }
    out.push("|");
    tickMap[m] = { startTick: tick, durTick: barTicks(ts) };
    tick += tickMap[m].durTick;
  }
  while (out[out.length - 1] === "|") out.pop();
  return { tex: `\\tempo ${headerTempo}\n\\instrument 30\n.\n${out.join(" ")}`, tickMap, headerTempo };
}
